import { buildTrainingsDeskPath } from "@tennis-matches/core";
import {
  collectOverdueBillingStatements,
  computeBillingStatementOverdueDays,
  loadBillingContacts,
  loadBillingStatementsForHub,
  mergePeopleWithMissingIds,
} from "../trainerstunden/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function personName(row) {
  const full = `${asStr(row?.first_name)} ${asStr(row?.last_name)}`.trim();
  if (full) return full;
  return asStr(row?.email) || asStr(row?.display_name);
}

function resolveRecipientName(statement, peopleById, contactsById) {
  const snap = asStr(statement?.recipient_name_snapshot);
  if (snap) return snap;
  const type = asStr(statement?.recipient_type);
  if (type === "contact") {
    const contact = contactsById.get(asStr(statement?.billing_contact_id));
    const name = personName(contact);
    if (name) return name;
  }
  const person = peopleById.get(asStr(statement?.person_id));
  const name = personName(person);
  if (name) return name;
  return "";
}

function recipientKey(statement) {
  const type = asStr(statement?.recipient_type);
  if (type === "contact") {
    const id = asStr(statement?.billing_contact_id);
    if (id) return `contact:${id}`;
  }
  const personId = asStr(statement?.person_id);
  if (personId) return `person:${personId}`;
  const snap = asStr(statement?.recipient_name_snapshot);
  if (snap) return `name:${snap.toLowerCase()}`;
  return `statement:${asStr(statement?.id)}`;
}

/**
 * @param {string} [statementId]
 * @param {string} [billingPersonId] person_id oder billing_contact_id
 */
function buildUnpaidAbrechnungHref(statementId = "", billingPersonId = "") {
  const sp = new URLSearchParams({ view: "unpaid" });
  const sid = asStr(statementId);
  const personId = asStr(billingPersonId);
  if (sid) sp.set("stmt", sid);
  if (personId) sp.set("billingPerson", personId);
  return `${buildTrainingsDeskPath("abrechnung")}?${sp.toString()}`;
}

/** @param {object} statement */
function resolveBillingPersonId(statement) {
  const type = asStr(statement?.recipient_type);
  if (type === "contact") {
    return asStr(statement?.billing_contact_id);
  }
  return asStr(statement?.person_id) || asStr(statement?.billing_contact_id);
}

/**
 * @param {object[]} statements
 * @param {{ people?: object[], contacts?: object[], now?: Date, maxRows?: number }} [ctx]
 */
export function buildHubOverdueBillsPeekView(
  statements,
  { people = [], contacts = [], now = new Date(), maxRows = 3 } = {},
) {
  const overdue = collectOverdueBillingStatements(statements, now).sort(
    (a, b) => {
      const openDiff = (Number(b?.open_eur) || 0) - (Number(a?.open_eur) || 0);
      if (openDiff) return openDiff;
      return asStr(a?.due_date).localeCompare(asStr(b?.due_date));
    },
  );

  const peopleById = new Map(
    (Array.isArray(people) ? people : [])
      .map((p) => [asStr(p?.id), p])
      .filter(([id]) => id),
  );
  const contactsById = new Map(
    (Array.isArray(contacts) ? contacts : [])
      .map((c) => [asStr(c?.id), c])
      .filter(([id]) => id),
  );

  /** @type {Map<string, { key: string, name: string, openEur: number, count: number, overdueDays: number, href: string }>} */
  const byRecipient = new Map();
  for (const st of overdue) {
    const key = recipientKey(st);
    const openEur = Math.max(0, Number(st?.open_eur) || 0);
    const days = computeBillingStatementOverdueDays(st, now);
    const existing = byRecipient.get(key);
    if (existing) {
      existing.openEur = Math.round((existing.openEur + openEur) * 100) / 100;
      existing.count += 1;
      existing.overdueDays = Math.max(existing.overdueDays, days);
      continue;
    }
    const sid = asStr(st?.id);
    byRecipient.set(key, {
      key,
      name: resolveRecipientName(st, peopleById, contactsById),
      openEur: Math.round(openEur * 100) / 100,
      count: 1,
      overdueDays: days,
      href: buildUnpaidAbrechnungHref(sid, resolveBillingPersonId(st)),
    });
  }

  const rows = [...byRecipient.values()].sort(
    (a, b) => b.openEur - a.openEur || b.overdueDays - a.overdueDays,
  );
  const totalOpenEur =
    Math.round(rows.reduce((sum, r) => sum + r.openEur, 0) * 100) / 100;

  return {
    count: overdue.length,
    recipientCount: rows.length,
    totalOpenEur,
    rows: rows.slice(0, Math.max(1, maxRows)),
    moreCount: Math.max(0, rows.length - Math.max(1, maxRows)),
    href: buildUnpaidAbrechnungHref(),
  };
}

const HUB_OVERDUE_CACHE_MS = 20000;
/** @type {Map<string, Promise<object|null>>} */
const __inflight = new Map();
/** @type {Map<string, { ts: number, value: object|null }>} */
const __cache = new Map();

export function clearHubOverdueBillsPeekCache() {
  __cache.clear();
  __inflight.clear();
}

registerHubCacheClear(clearHubOverdueBillsPeekCache);

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<object|null>}
 */
export async function loadHubOverdueBillsPeek({ force = false } = {}) {
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __cache.get(cacheKey);
    if (cached && now - cached.ts < HUB_OVERDUE_CACHE_MS) {
      return cached.value;
    }
    const inflight = __inflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const promise = __loadHubOverdueBillsPeekImpl()
    .then((value) => {
      // null = fachlich „keine Überfälligen“, kein Fehler – cachen.
      if (isHubReadScopeCurrent(started)) {
        __cache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__inflight.get(cacheKey) === promise) __inflight.delete(cacheKey);
    });

  __inflight.set(cacheKey, promise);
  return promise;
}

async function __loadHubOverdueBillsPeekImpl() {
  const statementsRes = await loadBillingStatementsForHub().catch(() => ({
    ok: false,
    data: [],
  }));
  const statements = statementsRes?.ok ? statementsRes.data || [] : [];
  const overdue = collectOverdueBillingStatements(statements);
  if (!overdue.length) return null;

  const needsPeople = overdue.some(
    (st) =>
      !asStr(st?.recipient_name_snapshot) &&
      asStr(st?.recipient_type) !== "contact" &&
      asStr(st?.person_id),
  );
  const needsContacts = overdue.some(
    (st) =>
      !asStr(st?.recipient_name_snapshot) &&
      asStr(st?.recipient_type) === "contact" &&
      asStr(st?.billing_contact_id),
  );

  let people = [];
  let contacts = [];
  if (needsPeople || needsContacts) {
    const [peopleRes, contactsRes] = await Promise.all([
      needsPeople
        ? mergePeopleWithMissingIds(
            [],
            overdue.map((st) => asStr(st?.person_id)).filter(Boolean),
          ).catch(() => null)
        : Promise.resolve(null),
      needsContacts
        ? loadBillingContacts().catch(() => ({ ok: false, data: [] }))
        : Promise.resolve({ ok: true, data: [] }),
    ]);
    if (peopleRes?.ok && Array.isArray(peopleRes.data)) people = peopleRes.data;
    if (contactsRes?.ok) contacts = contactsRes.data || [];
  }

  const view = buildHubOverdueBillsPeekView(statements, {
    people,
    contacts,
    maxRows: 6,
  });
  return view.count > 0 ? view : null;
}
